import React, { useState } from 'react';
import { Modal, Button as BsButton } from 'react-bootstrap';
import Button from 'components/base/Button';
import { ApiToken } from 'models/ApiToken';

type ApiTokenDeleteModalProps = {
  show: boolean;
  apiToken: ApiToken | null;
  onCancel: () => void;
  onConfirm: (id: number) => Promise<void>;
};

const ApiTokenDeleteModal: React.FC<ApiTokenDeleteModalProps> = ({
  show,
  apiToken,
  onCancel,
  onConfirm,
}) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = () => {
    if (deleting) return;
    setError(null);
    onCancel();
  };

  const handleConfirm = async () => {
    if (!apiToken) return;

    setDeleting(true);
    setError(null);
    try {
      await onConfirm(apiToken.id);
      onCancel();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete API token');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>Delete API Token</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div className="alert alert-outline-danger mb-3" role="alert">
            {error}
          </div>
        )}
        <p className="mb-2">
          Are you sure you want to delete this API token? This action cannot be undone.
        </p>
        {apiToken && (
          <p className="mb-0 text-700">
            <strong>{apiToken.description}</strong>
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <BsButton variant="secondary" onClick={handleCancel} disabled={deleting}>
          Cancel
        </BsButton>
        <Button
          variant="danger"
          onClick={handleConfirm}
          loading={deleting}
          loadingPosition="start"
          disabled={deleting || !apiToken}
          style={{ minWidth: '6.25rem' }}
        >
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApiTokenDeleteModal;
